import { useTranslation } from '@/hooks/useTranslation';
import { Button } from 'primereact/button';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './Breadcrumb.css';

export interface BreadcrumbItem {
  label: string;
  path?: string;
}

interface BreadcrumbProps {
  items: BreadcrumbItem[];
  showDeleteButton?: boolean;
  onDelete?: () => void;
  rightActions?: React.ReactNode;
}

export const Breadcrumb: React.FC<BreadcrumbProps> = ({
  items,
  showDeleteButton,
  onDelete,
  rightActions,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  // Determine home path based on current section
  const getHomePath = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    if (pathSegments[0] === 'projects') return '/projects';
    if (pathSegments[0] === 'prompts') return '/prompts';
    return '/surveys'; // Default to surveys
  };

  const handleNavigation = (
    e: React.MouseEvent<HTMLAnchorElement>,
    path: string
  ) => {
    e.preventDefault();
    navigate(path);
  };

  return (
    <div className='flex items-center justify-between bg-white border-b border-gray-200 px-6 min-h-[3.5rem]'>
      <nav aria-label={t('layout.breadcrumb.navigationLabel')}>
        <ol className='custom-breadcrumb-list'>
          {/* Home item */}
          <li className='custom-breadcrumb-item'>
            <a
              href={getHomePath()}
              onClick={e => handleNavigation(e, getHomePath())}
              className='custom-breadcrumb-link'
              aria-label={t('layout.breadcrumb.homeAriaLabel')}
            >
              <span className='pi pi-home' aria-hidden='true'></span>
            </a>
          </li>

          {/* Breadcrumb items */}
          {items.map((item, index) => {
            const isLast = index === items.length - 1;
            const isClickable = item.path && !isLast;

            return (
              <li key={index} className='custom-breadcrumb-item'>
                {/* Separator rendered inside li with presentation role */}
                <span
                  className='custom-breadcrumb-separator'
                  role='presentation'
                  aria-hidden='true'
                >
                  <span className='pi pi-chevron-right'></span>
                </span>

                {isClickable ? (
                  <a
                    href={item.path}
                    onClick={e => handleNavigation(e, item.path!)}
                    className='custom-breadcrumb-link'
                  >
                    {item.label}
                  </a>
                ) : (
                  <h1 className='custom-breadcrumb-current' aria-current='page'>
                    {item.label}
                  </h1>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
      <div className='flex items-center gap-2'>
        {rightActions}
        {showDeleteButton && onDelete && (
          <Button
            icon='pi pi-trash'
            text
            rounded
            onClick={onDelete}
            aria-label={t('layout.breadcrumb.deleteAriaLabel')}
          />
        )}
      </div>
    </div>
  );
};
